import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { ButtonWithScaleAnimation } from "by/gamefactory/cityversetycoon/scenes/common/components/ButtonWithScaleAnimation";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { FadeScreen } from "by/gamefactory/cityversetycoon/scenes/game/components/FadeScreen";
import { Container, IDestroyOptions, Texture } from "pixi.js";
import gsap from "gsap";
import { FullscreenInputBlocker } from "by/gamefactory/cityversetycoon/scenes/game/components/FullscreenInputBlocker";
import { MarketplaceGuestAvatar } from "../../../marketplace/main/components/fullbuilding/MarketplaceGuestAvatar";
import { UserProfileResourcePackage } from "../../../userprofile/resources/UserProfileResourcePackage";
import { IGuestModel } from "by/gamefactory/cityversetycoon/scenes/common/models/IGuestModel";
import { WalletFullBuildingInfo } from "./WalletFullBuildingInfo";
import { WalletFullBuildingDescription } from "./WalletFullBuildingDescription";
import { WalletFullBuilding } from "./WalletFullBuilding";
import { NFTBuilding } from "by/gamefactory/web3/nfts/NFTBuilding";
import { IPlayerModel } from "by/gamefactory/cityversetycoon/scenes/common/models/IPlayerModel";
import { StringFormatExtention } from "by/gamefactory/extensions/StringFormatExtention";
import { DynamicAssets } from "resources/DynamicAssets";

export class WalletFullBuildingContainer extends Container {
    private static readonly ANIMATION_DURATION = 0.3;

    private readonly canvasScaler: ICanvasScaler;
    private readonly commonResources: CommonResourcesPackage;
    private readonly animations: gsap.core.Timeline;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly guestModel: IGuestModel;
    private readonly playerModel: IPlayerModel;
    private readonly buildingResources: DynamicAssets;

    private fadeScreen: FadeScreen;
    private closeButton: ButtonWithScaleAnimation;
    private guestAvatar: MarketplaceGuestAvatar;
    private fullBuilding: WalletFullBuilding;
    private buildingInfo: WalletFullBuildingInfo;
    private buildingDescription: WalletFullBuildingDescription;

    public constructor(
        canvasScaler: ICanvasScaler,
        commonResources: CommonResourcesPackage,
        userProfileResources: UserProfileResourcePackage,
        guestModel: IGuestModel,
        playerModel: IPlayerModel,
        buildingResources: DynamicAssets
    ) {
        super();
        this.canvasScaler = canvasScaler;
        this.commonResources = commonResources;
        this.userProfileResources = userProfileResources;
        this.guestModel = guestModel;
        this.playerModel = playerModel;
        this.buildingResources = buildingResources;
        this.animations = new gsap.core.Timeline({ paused: true });
        this.build();
        this.layout();
        this.initAnimation();
        this.closeButton.onUp.add(this.closeButtonClickHandle, this);
    }

    private build(): void {
        this.fadeScreen = new FadeScreen(WalletFullBuildingContainer.ANIMATION_DURATION, 0, 0.85, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.fadeScreen.eventMode = 'static';
        this.closeButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_x_png);
        this.guestAvatar = new MarketplaceGuestAvatar(
            this.guestModel.avatarTexture.value,
            this.userProfileResources,
            this.guestModel.name.value,
            this.canvasScaler
        );
        this.buildingInfo = new WalletFullBuildingInfo();
        this.buildingDescription = new WalletFullBuildingDescription(this.commonResources);
        this.fullBuilding = new WalletFullBuilding();

        this.addChild(
            this.fadeScreen,
            this.closeButton,
            this.guestAvatar,
            this.buildingInfo,
            this.buildingDescription,
            this.fullBuilding
        );
    }

    private layout(): void {
        const width = this.canvasScaler.referenceWidth;
        this.closeButton.position.set(375, 1535);
        this.guestAvatar.position.set(width * 0.5 - this.guestAvatar.width * 0.5, 1112);
        this.buildingInfo.position.set(width * 0.5, 1262);
        this.buildingDescription.position.set((width - this.buildingDescription.width) * 0.5, 1390);
        this.fullBuilding.position.set(15, 258);
    }

    private initAnimation(): void {
        this.animations.fromTo(this, { alpha: 0 }, { alpha: 1, duration: WalletFullBuildingContainer.ANIMATION_DURATION, ease: 'power1.out' });
    }

    private async closeButtonClickHandle(): Promise<void> {
        await Promise.all([
            this.hide(),
            this.buildingDescription.hideTooltip()
        ]);
    }

    public async show(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        this.fadeScreen.fadeIn();
        await this.animations.play();
        this.removeChild(inputBlocker);
    }

    public async hide(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        await this.animations.reverse();
        this.visible = false;
        this.removeChild(inputBlocker);
    }

    public async update(building: NFTBuilding): Promise<void> {
        const buildingModel = this.playerModel.buildings.value.find((buildingModel) => buildingModel.id.value === building.buildingId);
        const texture = this.buildingResources.resource<Texture>(building.imageURL);
        this.fullBuilding.updateTexture(texture);
        this.fullBuilding.updateRank(buildingModel.level.value);
        this.buildingInfo.updateAddress(buildingModel.address.value, `${buildingModel.zipCode.value}, ${buildingModel.city.value}, ${buildingModel.borough.value.toUpperCase()}`);
        this.buildingDescription.updateDescription(
            buildingModel.piggyMultiplier.value,
            StringFormatExtention.formatWithComma(buildingModel.visitCostInCoinsUpTo.value),
            StringFormatExtention.formatWithComma(buildingModel.costPerSqft.value),
            StringFormatExtention.formatWithComma(buildingModel.sqft.value)
        );
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.closeButton.onUp.remove(this.closeButtonClickHandle, this);
        this.animations.kill();
        super.destroy(options);
    }
}
